import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FromLayout from "../../component/FromLayout";
import { useAuthUserMutation } from "../../store/api/authSlice";
import {
  capitalizeFirstLetter,
  handleApiError,
} from "../../utils/dateAndTimeConversion";
import { getToken } from "../../utils/localStorageMethods";
import { useTranslation } from "react-i18next";
import { getSubdomainRole } from "../../utils/getSubdomainRole";

const initialState = {
  full_name: "",
  company_name: "",
  email: "",
  password: "",
  role: "",
};

const Registration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isTokenExist = getToken();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const autoRole = getSubdomainRole();
  // const autoRole = subdomainRole === "vendor" ? "vendor" : "school";

  const [formData, setFormData] = useState({ ...initialState, role: autoRole });

  const [errors, setErrors] = useState({});
  const [authUser, { isLoading }] = useAuthUserMutation();

  const validateForm = () => {
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "role") return; // auto-set from subdomain
      if (!value)
        newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = t("invalidEmail");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    setErrors((prev) => ({
      ...prev,
      [name]: value ? "" : `${t("errors." + name)} ${t("errors.required")}`,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();

    try {
      const response = await authUser({
        user: formData,
        endPoint: "auth/register",
      }).unwrap();
      if (response.statusCode === 201) {
        toast.success(response.message);
        navigate("/");
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  // if (isTokenExist) {
  //   return <Navigate to="/dashboard" />;
  // }

  return (
    <FromLayout title="Sign Up" width="w-40">
      <div className="text-center mb-5">
        <p
          className="text-light"
          style={{ opacity: 0.8, maxWidth: "500px", margin: "0 auto" }}
        >
          {t("signup_get_started_note")}
        </p>
      </div>

      {/* Centered form container */}
      <div className="d-flex justify-content-center">
        <div style={{ width: "100%", maxWidth: "500px" }}>
          <form onSubmit={handleSubmit}>
            <div className="row g-3 text-start mb-4">
              <div className="col-12">
                <label className="form-label text-white">
                  {t("full")} {t("name")}
                </label>
                <input
                  type="text"
                  className="form-control mb-1"
                  placeholder={`${t("enter")} ${t("full")} ${t("name")}`}
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                />
                {errors.full_name && (
                  <span style={{ color: "red" }}>{errors.full_name}</span>
                )}
              </div>
            </div>

            <div className="row g-3 text-start mb-4">
              <div className="col-12">
                <label className="form-label text-white">
                  {t("company")} {t("name")}
                </label>
                <input
                  type="text"
                  className="form-control mb-1"
                  placeholder={`${t("enter")} ${t("company")} ${t("name")}`}
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                />
                {errors.company_name && (
                  <span style={{ color: "red" }}>{errors.company_name}</span>
                )}
              </div>
            </div>

            {/* Email Field */}
            <div className="row g-3 text-start mb-4">
              <div className="col-12">
                <label className="form-label text-white">
                  {t("email")} {t("address")}
                </label>
                <input
                  type="text"
                  className="form-control mb-1"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={`${t("enter")} ${t("your")} ${t("email")}`}
                />
                {errors.email && (
                  <span style={{ color: "red" }}>{errors.email}</span>
                )}
              </div>
            </div>

            {/* Role Field — auto-set from subdomain, hidden from user */}
            {/* <div className="row g-3 text-start mb-4">
              <div className="col-12">
                <label className="form-label text-white">{t("role")}</label>
                <select
                  className="form-control mb-1"
                  value={formData.role}
                  onChange={handleChange}
                  name="role"
                >
                  <option value="">{t("select")} {t("role")}</option>
                  <option value="school">{t("school")}</option>
                  <option value="vendor">{t("bus")} {t("vendor")}</option>
                </select>
                {errors.role && (
                  <span style={{ color: "red" }}>{errors.role}</span>
                )}
              </div>
            </div> */}

            {/* Password Field */}
            <div className="row g-3 text-start mb-4">
              <div className="col-12">
                <label className="form-label text-white">{t("password")}</label>
                <div className="input-group mb-1">
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    className="form-control"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={`${t("enter")} ${t("your")} ${t("password")}`}
                  />
                </div>
                {errors.password && (
                  <span style={{ color: "red" }}>{errors.password}</span>
                )}
              </div>
            </div>

            {/* Sign In Button */}
            <div className="row g-3 text-start mb-4">
              <div className="col-12">
                <button
                  type="submit"
                  className="btn btn-amber rounded-3 px-4 w-100"
                  disabled={isLoading}
                >
                  {isLoading ? t("signing_in") : t("sign_up")}
                </button>
              </div>
            </div>
            <div className="text-center mt-4">
              <span className="text-white" style={{ opacity: 0.9 }}>
                {t("already")} {t("have")} {t("an")} {t("account")}?{" "}
              </span>
              <Link
                to="/"
                className="hover-link"
                style={{
                  color: "#f0b241",
                  textDecoration: "none",
                  fontWeight: "500",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.textDecoration = "underline")
                }
                onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
              >
                {t("sign_in")}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </FromLayout>
  );
};

export default Registration;
